import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { SupabaseClient } from "@supabase/supabase-js";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { 
  Search, 
  BookOpen, 
  FileText, 
  GraduationCap,
} from "lucide-react";
import { SearchFilters } from "./SearchFilters";
import { SearchHistory } from "./SearchHistory";
import { SearchSuggestions } from "./SearchSuggestions";

const sb = supabase as unknown as SupabaseClient<any>;

interface GlobalSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GlobalSearch({ open, onOpenChange }: GlobalSearchProps) {
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState<{
    contentTypes: string[];
    difficulties: number[];
    dateRange: { from?: Date; to?: Date };
    scopeToCurrent: boolean;
  }>({
    contentTypes: [],
    difficulties: [],
    dateRange: {},
    scopeToCurrent: false,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Extract current topic from URL
  const currentTopicId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("topic");
  }, [location.search]);

  // Fetch topics
  const { data: topics = [] } = useQuery({
    queryKey: ["topics-search"],
    queryFn: async () => {
      const { data, error } = await sb.from("topics").select("id, name");
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
  });

  // Fetch MCQs
  const { data: mcqs = [] } = useQuery({
    queryKey: ["mcqs-search"],
    queryFn: async () => {
      const { data, error } = await sb
        .from("mcqs")
        .select("id, stem, tags, topic_id, difficulty")
        .eq("lifecycle_status", "active")
        .limit(50);
      if (error) throw error;
      return data as { id: string; stem: string; tags: string[] | null; topic_id: string; difficulty: number | null }[];
    },
  });

  // Fetch notes
  const { data: notes = [] } = useQuery({
    queryKey: ["notes-search"],
    queryFn: async () => {
      const { data, error } = await sb
        .from("notes")
        .select("id, title, content, topic_id")
        .limit(50);
      if (error) throw error;
      return data as { id: string; title: string; content: string; topic_id: string }[];
    },
  });

  // Save search to history
  const saveSearchMutation = useMutation({
    mutationFn: async (resultCount: number) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from("search_history").insert([{
        user_id: user.id,
        query,
        filters: filters as any,
        result_count: resultCount,
      }]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["search-history"] });
    },
  });

  // Filter results based on query and filters
  const filteredResults = useMemo(() => {
    if (!query) return { topics: [], mcqs: [], notes: [] };

    const lowerQuery = query.toLowerCase();
    const shouldInclude = (type: string) => 
      filters.contentTypes.length === 0 || filters.contentTypes.includes(type);

    let filteredTopics = topics;
    let filteredMcqs = mcqs;
    let filteredNotes = notes;

    // Apply scope filter
    if (filters.scopeToCurrent && currentTopicId) {
      filteredMcqs = filteredMcqs.filter(m => m.topic_id === currentTopicId);
      filteredNotes = filteredNotes.filter(n => n.topic_id === currentTopicId);
    }

    // Apply difficulty filter
    if (filters.difficulties.length > 0) {
      filteredMcqs = filteredMcqs.filter(m => 
        m.difficulty && filters.difficulties.includes(m.difficulty)
      );
    }

    return {
      topics: shouldInclude("topics") 
        ? filteredTopics.filter(t => t.name.toLowerCase().includes(lowerQuery)).slice(0, 5)
        : [],
      mcqs: shouldInclude("mcqs")
        ? filteredMcqs.filter(m => 
            m.stem.toLowerCase().includes(lowerQuery) ||
            m.tags?.some(tag => tag.toLowerCase().includes(lowerQuery))
          ).slice(0, 5)
        : [],
      notes: shouldInclude("notes")
        ? filteredNotes.filter(n => 
            n.title.toLowerCase().includes(lowerQuery) ||
            n.content.toLowerCase().includes(lowerQuery)
          ).slice(0, 5)
        : [],
    };
  }, [query, topics, mcqs, notes, filters, currentTopicId]);

  const handleSelect = (type: 'topic' | 'mcq' | 'note', id: string, topicId?: string) => {
    const totalResults = filteredResults.topics.length + filteredResults.mcqs.length + filteredResults.notes.length;
    saveSearchMutation.mutate(totalResults);

    onOpenChange(false);
    setQuery("");
    setFilters({ contentTypes: [], difficulties: [], dateRange: {}, scopeToCurrent: false });

    switch (type) {
      case 'topic':
        navigate(`/study/${id}`);
        break;
      case 'mcq':
        navigate(`/practice?topic=${topicId || ''}`);
        break;
      case 'note':
        navigate(`/notes?topic=${topicId || ''}`);
        break;
    }
  };

  const handleSelectHistory = (historyQuery: string, historyFilters: any) => {
    setQuery(historyQuery);
    setFilters(historyFilters || filters);
  };

  const handleSelectSuggestion = (suggestion: string) => {
    setQuery(suggestion);
  };

  // Listen for Cmd+K / Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onOpenChange(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onOpenChange]);

  const hasResults = filteredResults.topics.length > 0 || 
                     filteredResults.mcqs.length > 0 || 
                     filteredResults.notes.length > 0;

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput 
        placeholder="Search topics, MCQs, and notes..." 
        value={query}
        onValueChange={setQuery}
      />
      
      <div className="p-2 border-b">
        <SearchFilters filters={filters} onFilterChange={setFilters} />
      </div>

      <CommandList>
        {!query ? (
          <>
            <SearchHistory onSelectQuery={handleSelectHistory} />
            <CommandEmpty>
              <div className="text-center py-6">
                <Search className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-sm text-muted-foreground">
                  Start typing to search across all content
                </p>
                <Badge variant="outline" className="mt-2">
                  Cmd+K or Ctrl+K
                </Badge>
              </div>
            </CommandEmpty>
          </>
        ) : !hasResults ? (
          <CommandEmpty>
            <div className="text-center py-6">
              <p className="text-sm text-muted-foreground">
                No results found for "{query}"
              </p>
            </div>
          </CommandEmpty>
        ) : (
          <>
            <SearchSuggestions query={query} onSelectSuggestion={handleSelectSuggestion} />
            {filteredResults.topics.length > 0 && (
              <CommandGroup heading="Topics">
                {filteredResults.topics.map((topic) => (
                  <CommandItem
                    key={topic.id}
                    onSelect={() => handleSelect('topic', topic.id)}
                    className="flex items-center gap-3 cursor-pointer"
                  >
                    <GraduationCap className="h-4 w-4 text-primary" />
                    <span>{topic.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {filteredResults.mcqs.length > 0 && (
              <CommandGroup heading="MCQs">
                {filteredResults.mcqs.map((mcq) => (
                  <CommandItem
                    key={mcq.id}
                    onSelect={() => handleSelect('mcq', mcq.id, mcq.topic_id)}
                    className="flex items-start gap-3 cursor-pointer"
                  >
                    <BookOpen className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-sm">
                        {mcq.stem.substring(0, 100)}...
                      </div>
                      {mcq.tags && mcq.tags.length > 0 && (
                        <div className="flex gap-1 mt-1">
                          {mcq.tags.slice(0, 2).map((tag, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {filteredResults.notes.length > 0 && (
              <CommandGroup heading="Notes">
                {filteredResults.notes.map((note) => (
                  <CommandItem
                    key={note.id}
                    onSelect={() => handleSelect('note', note.id, note.topic_id)}
                    className="flex items-center gap-3 cursor-pointer"
                  >
                    <FileText className="h-4 w-4 text-primary flex-shrink-0" />
                    <span className="truncate">{note.title}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
